import { useEffect, useRef, useState } from "react";

/**
 * Returns the given boolean value, but delays propagating transitions to
 * `false` by `delayMs` milliseconds. Transitions to `true` are propagated
 * immediately, and a pending delayed deactivation is cancelled if the value
 * becomes `true` again before the delay elapses.
 *
 * This is useful to bridge a brief moment where a value flickers off, e.g.
 * when the pointer travels from a hover anchor onto a popover and neither is
 * reported as hovered for an instant.
 *
 * @param value The current boolean value.
 * @param delayMs The delay in milliseconds before a `false` value is applied.
 * @returns The value, with deactivation delayed by `delayMs`.
 */
export function useDelayedDeactivation(
  value: boolean,
  delayMs: number,
): boolean {
  const [isDelayedActive, setIsDelayedActive] = useState(value);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value) {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      setIsDelayedActive(true);
      return;
    }

    timeout.current = setTimeout(() => {
      setIsDelayedActive(false);
      timeout.current = null;
    }, delayMs);
  }, [value, delayMs]);

  // Clear any pending timer when the component unmounts.
  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    },
    [],
  );

  // Activate immediately (synchronously) when the value is true so there is no
  // perceived delay when opening. The delayed state only keeps the result true
  // while a pending deactivation is still counting down.
  return value || isDelayedActive;
}
